import { type BoulevardReceiptView, formatCents } from "@vaulted/shared";

/** A visit's checkout as Boulevard recorded it: each line (before and after discounts), the tip, and how it was paid. */
export function BoulevardReceipt({ r, className = "" }: { r: BoulevardReceiptView; className?: string }) {
  const tips = r.lines.filter((l) => l.kind === "tip");
  const others = r.lines.filter((l) => l.kind !== "tip");
  return (
    <div className={`flex flex-col gap-1 text-xs text-ink-soft ${className}`}>
      <div className="flex justify-between gap-3 font-semibold text-ink">
        <span>Boulevard receipt{r.orderNumber ? ` #${r.orderNumber}` : ""}</span>
        {r.voided && <span className="text-danger">Voided</span>}
      </div>
      {others.map((l, i) => (
        <div key={i} className="flex flex-col">
          <span className="flex justify-between gap-3">
            <span>
              {l.name}
              {l.quantity > 1 && ` × ${l.quantity}`}
              {l.addOns.length > 0 && <span className="text-muted"> + {l.addOns.join(", ")}</span>}
            </span>
            <span className="tabular-nums">
              {l.beforeCents !== l.cents && <s className="mr-1.5 text-muted">{formatCents(l.beforeCents)}</s>}
              {formatCents(l.cents)}
            </span>
          </span>
          {l.discounts.map((d, j) => (
            <span key={j} className="pl-3 text-muted">
              {d.name}: −{formatCents(d.cents)}
            </span>
          ))}
          {l.refundedCents > 0 && <span className="pl-3 text-danger">Refunded {formatCents(l.refundedCents)}</span>}
        </div>
      ))}
      {tips.map((l, i) => (
        <span key={`t${i}`} className="flex justify-between gap-3">
          <span>{l.name}</span>
          <span className="tabular-nums">{formatCents(l.cents)}</span>
        </span>
      ))}
      {(r.taxCents > 0 || r.feeCents > 0) && (
        <span className="flex justify-between gap-3">
          <span>{[r.taxCents > 0 && "Tax", r.feeCents > 0 && "Fees"].filter(Boolean).join(" and ")}</span>
          <span className="tabular-nums">{formatCents(r.taxCents + r.feeCents)}</span>
        </span>
      )}
      <span className="flex justify-between gap-3 border-t border-mist pt-0.5 font-semibold text-ink">
        <span>{r.shared ? "Total for everyone on this checkout" : "Total"}</span>
        <span className="tabular-nums">{formatCents(r.totalCents)}</span>
      </span>
      {r.payments.map((p, i) => (
        <span key={`p${i}`} className="flex justify-between gap-3">
          <span>
            Paid by {p.card ?? p.label}
            {p.refundedCents > 0 && <span className="text-danger"> (refunded {formatCents(p.refundedCents)})</span>}
          </span>
          <span className="tabular-nums">{formatCents(p.cents)}</span>
        </span>
      ))}
      {r.refundedCents > 0 && (
        <span className="flex justify-between gap-3 text-danger">
          <span>Refunded</span>
          <span className="tabular-nums">−{formatCents(r.refundedCents)}</span>
        </span>
      )}
    </div>
  );
}

